import { inject, Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import {
  Observable,
  throwError,
  catchError,
  from,
  switchMap,
  retry,
  timer,
} from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { LockNavigationService } from 'src/app/shared/services/lock-navigation.service';
import { environment } from 'src/environments/environment';
import { SnackbarService } from 'src/app/shared/utils/snackbar/snackbar.service';

interface Profile extends Keycloak.KeycloakProfile {
  attributes: {
    company: string[];
    wasticsUserId: string[];
  };
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  keycloakService = inject(KeycloakService);
  snackbarService = inject(SnackbarService);
  lockNavigationService = inject(LockNavigationService);

  /** The 428 fires for many endpoints at once, so we surface the "account not ready" hint only
   *  once per page load instead of spamming a snackbar per request. */
  private attributesWarningShown = false;

  /**
   * "Is the session still good?" probe endpoints. A 401/403 here is an
   * unambiguous session-dead signal — UNLIKE a 401/403 from a feature endpoint
   * (lacking permission for one resource), which must not log the user out.
   */
  private isAuthProbe(url: string | null | undefined): boolean {
    const u = url ?? '';
    return (
      /\/v[12]\/(me|user-sessions)(\/|$|\?)/.test(u) || /\/v2\/auth\//.test(u)
    );
  }

  private handleAuthError(request: HttpErrorResponse): Observable<any> {
    console.error('[TokenInterceptor] Auth error handler:', {
      status: request.status,
      statusText: request.statusText,
      url: request.url,
      message: request.message,
      error: request.error,
    });

    // Auto-logout ONLY for "is the session still good?" probes — those are
    // unambiguous session-dead signals. Random 401/403 from feature endpoints
    // (notifications, services we don't run on this stack, lacking permission
    // for one resource) must not kick the user out of the whole app.
    const url = request.url ?? '';
    const isAuthProbeEndpoint = this.isAuthProbe(url);
    if (
      isAuthProbeEndpoint &&
      (request.status === HttpStatusCode.Unauthorized ||
        request.status === HttpStatusCode.Forbidden)
    ) {
      console.warn(
        '[TokenInterceptor] Auth-probe 401/403 — logging out user',
        url
      );
      this.lockNavigationService.unlock();
      this.keycloakService.logout(window.location.toString());
    } else if (
      request.status === HttpStatusCode.Unauthorized ||
      request.status === HttpStatusCode.Forbidden
    ) {
      console.warn(
        '[TokenInterceptor] 401/403 on feature endpoint — staying signed in',
        url
      );
    }

    if (
      request.status === HttpStatusCode.PreconditionRequired &&
      (request.url.includes(environment.quarkusApiUrl) ||
        request.url.includes(environment.dwrmBackendV2Url))
    ) {
      console.log(
        '[TokenInterceptor] 412 Precondition Required - force token refresh'
      );
      this.keycloakService
        .getKeycloakInstance()
        .updateToken(-1)
        .then(() => {
          console.log('[TokenInterceptor] Token forcefully refreshed');
          this.keycloakService.loadUserProfile().then((userprofile) => {
            // `attributes` can be undefined for a freshly-provisioned account whose Keycloak
            // company/wasticsUserId attributes haven't synced yet — guard so we never throw a
            // TypeError here (which previously left the dashboard hanging).
            const attributes = (userprofile as Profile).attributes;
            const company = attributes?.company;
            const wasticsUserId = attributes?.wasticsUserId;
            if (!(company?.[0] && wasticsUserId?.[0])) {
              console.warn(
                '[TokenInterceptor] User missing company or wasticsUserId attributes'
              );
              if (!this.attributesWarningShown) {
                this.attributesWarningShown = true;
                this.snackbarService.open({
                  type: 'error',
                  message:
                    'Dein Zugang wird gerade fertig eingerichtet. Bitte lade die Seite in ein paar Sekunden neu. Falls es weiterhin nicht klappt, wende dich an den Support.',
                  duration: 10000,
                });
              }
            }
          });
        })
        .catch((error) => {
          console.error('[TokenInterceptor] Force token refresh failed', error);
        });
    }

    return throwError(() => request);
  }

  private handleRequest(
    next: HttpHandler,
    request: HttpRequest<any>
  ): Observable<HttpEvent<any>> {
    const isProbe = this.isAuthProbe(request.url);
    return next.handle(request).pipe(
      // On a brand-new self-service login the local user record is created lazily
      // on the first backend request; a near-simultaneous /v2/me probe can race
      // ahead of that and get a transient 401. Give the probe ONE delayed retry
      // before treating the 401 as a dead session — by the retry the record
      // exists. A genuinely dead session still fails the retry and logs out.
      retry({
        count: 1,
        delay: (error: HttpErrorResponse) => {
          if (
            isProbe &&
            (error.status === HttpStatusCode.Unauthorized ||
              error.status === HttpStatusCode.Forbidden)
          ) {
            return timer(1000);
          }
          return throwError(() => error);
        },
      }),
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === HttpStatusCode.Unauthorized ||
          error.status === HttpStatusCode.Forbidden ||
          error.status === HttpStatusCode.PreconditionRequired
        ) {
          return this.handleAuthError(error);
        }
        return throwError(() => error);
      })
    );
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from(this.keycloakService.updateToken(30)).pipe(
      catchError((error) => {
        console.error('[TokenInterceptor] Token update failed', error);
        return throwError(() => error);
      }),
      switchMap((updated) => {
        if (updated) {
          // Token was refreshed, clone the request with the new token
          return from(this.keycloakService.getToken()).pipe(
            switchMap((token: string) => {
              const authReq = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${token}`,
                },
              });
              return this.handleRequest(next, authReq);
            })
          );
        } else {
          // Token is still valid, proceed without modifying the request
          return this.handleRequest(next, request);
        }
      })
    );
  }
}
